import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import genericHelpers from 'customer_pages/_utils/generic-helpers';
import get from 'lodash/get';
import FocusTrap from 'focus-trap-react';
import ZipCodes from '../_libs/zip-codes';
import StoreInfo from './store-info';
import {
  isPostalCodeLocal,
  shouldStripPostalCodeWhitespace,
} from '../_utils/validations';
import Spinner from '../_utils/spinner';
import ConfirmStore from './confirm-store';
import { useStorePickerContext } from './store-picker-context';

const StorePicker = ({ isOpen, onClose }) => {
  const {
    zipCode: zipCodeFromContext,
    locations,
    geolocationToZipcode,
    productStockToLocationUrl,
    selectedDeliveryOption,
    copies,
    deliveryType,
    handleOnSave,
    prismId,
  } = useStorePickerContext();

  const [zipCode, setZipCode] = useState(zipCodeFromContext || '');
  const [selectedStoreId, setSelectedStoreId] = useState(locations.selected);
  // Initialise from already-fetched locations — no duplicate request on open
  const [storesState, setStoresState] = useState(locations.pickup || []);
  const [isLoadingState, setIsLoadingState] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const modalPortal = document.getElementById('react-store-picker');

  // Keep local state in sync if context changes while modal is open
  useEffect(() => {
    setSelectedStoreId(locations.selected);
  }, [locations.selected]);

  useEffect(() => {
    setZipCode(zipCodeFromContext);
  }, [zipCodeFromContext]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const fetchGroupedLocations = async (code) => {
    if (!productStockToLocationUrl || !code) {
      return;
    }
    setIsLoadingState(true);
    try {
      const newLocations = await genericHelpers.findLocationsForGroupedPages(
        productStockToLocationUrl,
        code,
        prismId,
        selectedDeliveryOption,
        copies,
      );
      setIsLoadingState(false);
      setStoresState(newLocations);
      if (
        !selectedStoreId ||
        !newLocations.some((location) => location.id === selectedStoreId)
      ) {
        setSelectedStoreId(get(newLocations[0], 'id'));
      }
    } catch {
      setIsLoadingState(false);
      setStoresState([]);
    }
  };

  const existingStore = locations.pickup.find(
    (pickup) => pickup.id === locations.selected,
  );

  const newSelectedStore = storesState.find(
    (store) => store.id === selectedStoreId,
  );

  const handleChangeZipcode = (event) => {
    const value = event.target.value;
    const processed = shouldStripPostalCodeWhitespace() ? value.replace(/ /g, '') : value;
    setStoresState([]);
    setZipCode(processed);
    if (isPostalCodeLocal(processed)) {
      fetchGroupedLocations(processed);
    }
  };

  const saveStore = () => {
    if (zipCode !== zipCodeFromContext) {
      handleOnSave(selectedStoreId, zipCode);
    } else {
      handleOnSave(selectedStoreId);
    }
    onClose();
  };

  const handleSave = () => {
    if (
      locations.storeSource === 'cart' ||
      locations.storeSource === 'order'
    ) {
      setShowConfirmation(true);
    } else {
      saveStore();
    }
  };

  const handleCurrentLocationClick = () => {
    if (geolocationToZipcode && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        ({ coords: { latitude, longitude } }) => {
          ZipCodes.fromLatLong(
            geolocationToZipcode,
            latitude,
            longitude,
            ({ zipcode: newZipCode }) => {
              setZipCode(newZipCode);
              fetchGroupedLocations(newZipCode);
            },
          );
        },
      );
    } else {
      alert('Geolocation is not supported by this browser.');
    }
  };

  const cancelChange = () => {
    setShowConfirmation(false);
  };

  return (
    <>
      {ReactDOM.createPortal(
        <FocusTrap>
          <div
            className="modal d-block store-picker-modal"
            tabIndex="-1"
            role="dialog">
            <div className="modal-dialog store-picker" role="document">
              <div className="modal-content right-popup">
                <div className="d-block top-border d-flex justify-content-between">
                  <h1 className="ml-4 mt-2">Select your store</h1>
                  <button
                    type="button"
                    className="btn btn-close-popup pr-4"
                    aria-label="Close location search"
                    onClick={onClose}>
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div className="store-modal-header mt-2">
                  <h5 className="strong mt-2">Enter zip code or city, state</h5>
                  <div className="mb-3 input-group-zipcode">
                    <div className="input-group-prepend">
                      <i className="fa fa-search zip-input-icon"></i>
                    </div>
                    <input
                      type="text"
                      className="form-control zip-input"
                      placeholder="Enter zip code or city, state"
                      value={zipCode}
                      onChange={handleChangeZipcode}
                      aria-label="Enter zip code"
                      aria-describedby="store location heading"
                    />
                    <div className="input-group-append">
                      <button
                        className="btn close btn-zipcode-empty"
                        type="button"
                        aria-label="Clear zip code"
                        onClick={() => setZipCode('')}>
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                  </div>
                  <button
                    className="btn btn-link mb-1 p-0 btn-map"
                    onClick={handleCurrentLocationClick}
                    aria-label="Use my current location">
                    <i className="fas fa-map-marker-alt mr-2 "></i>Use my current
                    location
                  </button>
                  {isLoadingState && <Spinner />}
                </div>
                <div className="modal-body p-4">
                  {!isLoadingState &&
                    storesState.length === 0 &&
                    isPostalCodeLocal(zipCode) && (
                      <p>Could not find near by store for zipcode {zipCode}</p>
                    )}
                  <div className="">
                    {storesState.map((store) => {
                      const isAvailable =
                        store?.orderTimestamps?.[deliveryType]?.['888888']
                          ?.service_available || false;
                      return (
                        <StoreInfo
                          key={store.id}
                          store={store}
                          isSelected={selectedStoreId === store.id}
                          serviceAvailable={isAvailable}
                          onSelect={(id) => {
                            setSelectedStoreId(id);
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
                <div className="modal-footer-save">
                  <div className="text-center mt-4">
                    <button
                      className="btn btn-primary btn-lg btn-block btn-save"
                      onClick={handleSave}>
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </FocusTrap>,
        modalPortal,
      )}
      {showConfirmation &&
        ReactDOM.createPortal(
          <FocusTrap>
            <div
              className="modal d-flex align-items-center justify-content-center store-confirm-modal"
              tabIndex="-1"
              role="dialog">
              <div className="modal-dialog" role="document">
                <ConfirmStore
                  currentStore={existingStore}
                  newStore={newSelectedStore}
                  cancelChange={cancelChange}
                  saveStore={saveStore}
                />
              </div>
            </div>
          </FocusTrap>,
          modalPortal,
        )}
    </>
  );
};

StorePicker.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default StorePicker;
